
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { CheckCircle } from 'lucide-react';
import { PayPalConfigDialog } from './PayPalConfigDialog';
import { toast } from 'sonner';

interface PayPalPaymentMethodProps {
  enabled: boolean;
  onToggle: (checked: boolean) => void;
  disabled: boolean;
  shopId: string;
}

export const PayPalPaymentMethod: React.FC<PayPalPaymentMethodProps> = ({
  enabled,
  onToggle,
  disabled,
  shopId
}) => {
  const [showPaypalConfig, setShowPaypalConfig] = useState(false);
  const [hasCredentials, setHasCredentials] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    // Vérifier si les identifiants PayPal existent déjà
    const checkPayPalCredentials = async () => {
      try {
        setIsChecking(true);
        const response = await fetch(`/api/payment-credentials?shopId=${shopId}&provider=paypal`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        });
        
        const data = await response.json();
        setHasCredentials(data.hasCredentials || false);
      } catch (error) {
        console.error('Erreur lors de la vérification des identifiants PayPal:', error);
      } finally {
        setIsChecking(false);
      }
    };

    checkPayPalCredentials();
  }, [shopId, showPaypalConfig]);

  const handleToggle = (checked: boolean) => {
    if (checked && !hasCredentials) {
      // Si l'utilisateur essaie d'activer sans avoir configuré les identifiants
      toast.error('Veuillez configurer vos identifiants PayPal avant d\'activer ce mode de paiement');
      setShowPaypalConfig(true);
      return;
    }
    
    // Autoriser la désactivation même si les identifiants sont configurés
    onToggle(checked);
  };

  return (
    <div className="border rounded-lg">
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-full bg-blue-100">
            <CheckCircle className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-medium">PayPal</h3>
            <p className="text-sm text-muted-foreground">
              Acceptez les paiements via PayPal
            </p>
          </div>
        </div>
        <div className="flex space-x-2 items-center">
          <Button 
            variant="outline" 
            size="sm"
            className="whitespace-nowrap"
            onClick={() => setShowPaypalConfig(true)}
          >
            Configurer
          </Button>
          <Switch
            checked={enabled}
            onCheckedChange={handleToggle}
            disabled={disabled || (isChecking && !hasCredentials)}
          />
        </div>
      </div>
      <PayPalConfigDialog 
        shopId={shopId}
        open={showPaypalConfig}
        onOpenChange={setShowPaypalConfig}
        onSaved={() => setHasCredentials(true)}
      />
    </div>
  );
};
